import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Droplets, CloudRain } from "lucide-react";

const G = { stem: "#2f7a46", leaf: "#5fb673", leafDark: "#276539", canopy: "#3d9a55", petal: "#d7262c", petalDark: "#b3151d", petalLight: "#e94b45", sun: "#fcd34d", pot: "#2b6b5b", potDark: "#1f5a4c", soil: "#6b4a32", water: "#7dd3fc", mountain: "#c6d0c8" };

function Leaf({ x, y, side, scale = 1, dark }) {
  const dir = side === "left" ? -1 : 1;
  return <path d={`M${x} ${y} q ${dir * 14 * scale} ${-4 * scale} ${dir * 22 * scale} ${-14 * scale} q ${-dir * 16 * scale} ${2 * scale} ${-dir * 22 * scale} ${14 * scale} z`} fill={dark ? G.leafDark : G.leaf} opacity={dark ? 0.9 : 1} />;
}

/** Spiral path (archimedean) in local coords, used for the rose's cream swirl like the brand mark. */
function spiral(x, y, r) {
  const pts = [];
  for (let i = 0; i <= 36; i++) {
    const t = (i / 36) * Math.PI * 3.2;
    const rr = (t / (Math.PI * 3.2)) * r * 0.78;
    pts.push(`${(x + Math.cos(t) * rr).toFixed(2)} ${(y + Math.sin(t) * rr).toFixed(2)}`);
  }
  return `M${pts.join(" L")}`;
}

function Flower({ x, y, r = 7 }) {
  // a red rose with a cream spiral — matches the Root & Rose mark
  return (
    <g data-testid="garden-rose">
      {[0, 72, 144, 216, 288].map((a) => <ellipse key={a} cx={x + Math.cos((a * Math.PI) / 180) * r * 0.5} cy={y + Math.sin((a * Math.PI) / 180) * r * 0.5} rx={r * 0.8} ry={r * 0.58} fill={G.petal} transform={`rotate(${a + 20} ${x + Math.cos((a * Math.PI) / 180) * r * 0.5} ${y + Math.sin((a * Math.PI) / 180) * r * 0.5})`} />)}
      <circle cx={x} cy={y} r={r * 0.82} fill={G.petalLight} />
      <path d={spiral(x, y, r)} stroke="#fdf6ee" strokeWidth={Math.max(0.6, r * 0.13)} fill="none" strokeLinecap="round" />
      <path d={`M${x - r * 0.95} ${y + r * 0.55} q ${-r * 0.6} ${r * 0.2} ${-r * 0.4} ${r * 0.9}`} stroke={G.leafDark} strokeWidth="1.2" fill="none" />
      <circle cx={x} cy={y} r={r * 0.12} fill={G.petalDark} />
    </g>
  );
}

const MAX_DROPS = 8;
const RAIN_AT = 5;
const RAIN_MS = 4000;
const DROP_X = [38, 52, 66, 80, 45, 73, 59, 86];

/** One falling droplet per community member who watered you today (capped), looping gently onto the pot. */
function WaterDrops({ count }) {
  const n = Math.min(MAX_DROPS, count);
  if (!n) return null;
  return (
    <g data-testid="garden-drops" data-count={n}>
      {Array.from({ length: n }).map((_, i) => (
        <motion.path key={i} d={`M${DROP_X[i]} 0 c -3 5 -4 7 -4 9.5 a 4 4 0 0 0 8 0 c 0 -2.5 -1 -4.5 -4 -9.5 z`} fill="#38bdf8" opacity="0.85" data-testid={`garden-drop-${i}`}
          initial={{ y: 60 + (i % 3) * 10, opacity: 0 }}
          animate={{ y: [60 + (i % 3) * 10, 118], opacity: [0, 0.9, 0.9, 0] }}
          transition={{ duration: 1.6 + (i % 4) * 0.25, repeat: Infinity, repeatDelay: 1.2 + (i % 5) * 0.4, delay: i * 0.45, ease: "easeIn" }} />
      ))}
    </g>
  );
}

/** Rain day: a short shower of soft drops plus sparkles on the leaves when 5+ people watered you today. */
function RainShower({ top }) {
  return (
    <g data-testid="garden-rain">
      {Array.from({ length: 18 }).map((_, i) => (
        <motion.line key={i} x1={8 + i * 6} x2={6 + i * 6} y1={0} y2={10} stroke="#7dd3fc" strokeWidth="1.6" strokeLinecap="round" opacity="0.7"
          initial={{ y: -10 }} animate={{ y: [-10, 125], opacity: [0, 0.8, 0.8, 0] }}
          transition={{ duration: 0.9 + (i % 3) * 0.2, repeat: Infinity, delay: (i % 6) * 0.15, ease: "linear" }} />
      ))}
      {[[42, top + 10], [78, top + 14], [60, top - 8], [50, top + 30], [72, top + 34]].map(([x, y], i) => (
        <motion.path key={i} d={`M${x} ${y - 5} L${x + 1.4} ${y - 1.4} L${x + 5} ${y} L${x + 1.4} ${y + 1.4} L${x} ${y + 5} L${x - 1.4} ${y + 1.4} L${x - 5} ${y} L${x - 1.4} ${y - 1.4} Z`} fill="#fef3c7" stroke="#fcd34d" strokeWidth="0.6"
          initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 1.2, 0], opacity: [0, 1, 0] }} style={{ transformOrigin: `${x}px ${y}px` }}
          transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.3 }} data-testid={`garden-sparkle-${i}`} />
      ))}
    </g>
  );
}

/** Plays the shower for RAIN_MS on mount and whenever the water count grows past the threshold, or when a community Rain Day is on. */
function useRain(count, rainDay) {
  const [raining, setRaining] = useState(false);
  useEffect(() => {
    if (count < RAIN_AT && !rainDay) return;
    setRaining(true);
    const t = setTimeout(() => setRaining(false), RAIN_MS);
    return () => clearTimeout(t);
  }, [count, rainDay]);
  return raining;
}

/** Growing plant: `stage` 0-9 (level index - 1), `progress` 0-1 fills the pot with water toward the next level. */
export function GardenPlant({ stage = 0, progress = 0, className = "", animate = true, drops = 0, rainDay = false, century = false }) {
  const raining = useRain(animate ? drops : 0, animate && rainDay);
  const s = Math.max(0, Math.min(9, stage));
  const top = 118 - (22 + s * 9);
  const leaves = Math.min(s + 1, 6);
  const Wrap = animate ? motion.svg : "svg";
  const anim = animate ? { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } } : {};
  return (
    <Wrap viewBox="0 0 120 160" className={className} data-testid="garden-plant" data-stage={s} {...anim}>
      <defs>
        <clipPath id={`pot-${s}`}><path d="M30 122 L90 122 L84 152 L36 152 Z" /></clipPath>
        <radialGradient id="glow"><stop offset="0%" stopColor={G.sun} stopOpacity="0.55" /><stop offset="100%" stopColor={G.sun} stopOpacity="0" /></radialGradient>
      </defs>
      {s >= 9 && <circle cx="60" cy={top - 6} r="46" fill="url(#glow)" />}
      {s >= 8 && <path d="M0 122 L28 70 L46 100 L60 84 L82 122 Z" fill={G.mountain} opacity="0.55" />}
      {s >= 9 && [0, 45, 90, 135].map((a) => <line key={a} x1={60 - Math.cos((a * Math.PI) / 180) * 40} y1={top - 6 - Math.sin((a * Math.PI) / 180) * 40} x2={60 + Math.cos((a * Math.PI) / 180) * 40} y2={top - 6 + Math.sin((a * Math.PI) / 180) * 40} stroke={G.sun} strokeWidth="1.5" opacity="0.5" />)}
      {/* pot with water level = progress */}
      <path d="M30 122 L90 122 L84 152 L36 152 Z" fill={G.pot} />
      <rect x="30" y={152 - 30 * Math.max(0.04, progress)} width="60" height={30 * Math.max(0.04, progress)} fill={G.water} opacity="0.55" clipPath={`url(#pot-${s})`} data-testid="garden-progress" />
      <rect x="26" y="116" width="68" height="8" rx="3" fill={G.potDark} />
      <ellipse cx="60" cy="120" rx="30" ry="4" fill={G.soil} />
      {/* stem */}
      <path d={`M60 120 C ${s % 2 ? 54 : 66} ${(120 + top) / 2} ${s % 2 ? 64 : 56} ${(120 + top) / 2} 60 ${top}`} stroke={G.stem} strokeWidth={s >= 7 ? 7 : s >= 5 ? 5 : 3.5} fill="none" strokeLinecap="round" />
      {/* leaves along the stem */}
      {Array.from({ length: leaves }).map((_, i) => {
        const y = 112 - (i + 1) * ((112 - top) / (leaves + 1));
        return <Leaf key={i} x={60} y={y} side={i % 2 ? "left" : "right"} scale={0.7 + Math.min(s, 4) * 0.12} dark={i % 3 === 2} />;
      })}
      {/* canopy from Grove onward */}
      {s >= 5 && <g>
        <circle cx="60" cy={top + 4} r={16 + (s - 5) * 3} fill={G.canopy} />
        <circle cx={44 - (s - 5)} cy={top + 14} r={12 + (s - 5) * 2} fill={G.leaf} />
        <circle cx={76 + (s - 5)} cy={top + 14} r={12 + (s - 5) * 2} fill={G.leafDark} opacity="0.9" />
      </g>}
      {/* bloom */}
      {s >= 3 && s < 5 && <Flower x={60} y={top - 2} r={6 + (s - 3) * 2} />}
      {s >= 6 && [[-14, 10], [12, 4], [0, -10], [-6, 20], [18, 16]].slice(0, 3 + Math.min(s - 6, 2)).map(([dx, dy], i) => <Flower key={i} x={60 + dx} y={top + 6 + dy} r={4 + (s >= 8 ? 1 : 0)} />)}
      {s === 0 && <circle cx="60" cy={top} r="2.5" fill={G.leaf} />}
      {century && <g data-testid="garden-century-stone"><ellipse cx="104" cy="150" rx="13" ry="8" fill="#8a8f86" /><ellipse cx="104" cy="148" rx="12" ry="7" fill="#b8bdb4" />
        <text x="104" y="151" textAnchor="middle" fontSize="7" fontWeight="700" fill="#44403c" fontFamily="Georgia, serif">100</text></g>}
      {animate && (raining ? <RainShower top={top} /> : <WaterDrops count={drops} />)}
    </Wrap>
  );
}

/** "Watered today by A, B and 2 others" — or "Rain day!" from 5 waters, or a community Rain Day. */
export function WateredBy({ waters, rainDay, className = "" }) {
  if (rainDay) {
    return (
      <div className={`flex items-center gap-1.5 text-[11px] font-semibold text-sky-800 ${className}`} data-testid="garden-watered-by">
        <CloudRain className="w-3.5 h-3.5" />
        <span data-testid="garden-community-rain">Rain Day in the <span className="capitalize">{rainDay.category}</span> garden · +{rainDay.points} pts</span>
      </div>
    );
  }
  if (!waters?.count) return null;
  if (waters.count >= RAIN_AT) {
    return (
      <div className={`flex items-center gap-1.5 text-[11px] font-semibold text-sky-800 ${className}`} data-testid="garden-watered-by">
        <CloudRain className="w-3.5 h-3.5" />
        <span data-testid="garden-rain-day">Rain day! Watered by {waters.count} people</span>
      </div>
    );
  }
  const names = waters.handles.slice(0, 2);
  const rest = waters.count - names.length;
  const who = names.length === 1 ? names[0] : names.join(names.length === 2 && !rest ? " and " : ", ");
  return (
    <div className={`flex items-center gap-1.5 text-[11px] text-sky-800 ${className}`} data-testid="garden-watered-by">
      <Droplets className="w-3.5 h-3.5" />
      <span>Watered today by <strong>{who}</strong>{rest > 0 && <> and {rest} other{rest === 1 ? "" : "s"}</>}</span>
    </div>
  );
}

export function GardenHeader({ game, onOpen }) {
  if (!game) return null;
  const { level, points_total } = game;
  const nextName = game.levels?.[level.index]?.name;
  return (
    <button onClick={onOpen} data-testid="garden-header" className="w-full warm-card p-3 sm:p-4 flex items-center gap-4 text-left hover:border-amber-300 transition-colors">
      <GardenPlant stage={level.index - 1} progress={level.progress} drops={game.waters_today?.count || 0} rainDay={!!game.rain_day} century={!!game.milestones?.century} className="w-14 h-[74px] shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Your garden · Level {level.index}</div>
        <div className="font-display text-xl font-bold text-stone-900 leading-tight" data-testid="garden-level-name">{level.name}</div>
        <div className="mt-1.5 h-1.5 rounded-full bg-stone-100 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-700" style={{ width: `${Math.max(3, level.progress * 100)}%` }} /></div>
        <div className="text-[11px] text-stone-500 font-mono mt-1" data-testid="garden-next">{level.next_min ? `${level.next_min - points_total} pts until ${nextName}` : "Fully grown · Luminary"}</div>
        <WateredBy waters={game.waters_today} rainDay={game.rain_day} className="mt-1" />
      </div>
    </button>
  );
}

export function GardenCard({ game }) {
  if (!game) return null;
  const { level, levels = [], points_total } = game;
  return (
    <section className="warm-card p-6" data-testid="garden-card">
      <div className="flex flex-col sm:flex-row gap-6 items-center">
        <GardenPlant stage={level.index - 1} progress={level.progress} drops={game.waters_today?.count || 0} rainDay={!!game.rain_day} century={!!game.milestones?.century} className="w-40 h-52 shrink-0" />
        <div className="flex-1 min-w-0 w-full">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Your garden</div>
          <h3 className="font-display text-2xl font-bold text-stone-900">{level.name} <span className="text-sm font-sans font-semibold text-stone-500">· {points_total} pts</span></h3>
          <p className="text-sm text-stone-600 mt-1">Every check-in waters the pot. When it fills, your plant grows into the next stage{level.next_min ? ` — ${level.next_min - points_total} pts to go` : ""}.</p>
          <WateredBy waters={game.waters_today} rainDay={game.rain_day} className="mt-2 text-xs" />
          <div className="mt-4 grid grid-cols-5 sm:grid-cols-10 gap-2" data-testid="garden-level-path">
            {levels.map((l, i) => {
              const reached = i < level.index, current = i === level.index - 1;
              return (
                <div key={l.name} title={`${l.name} · ${l.min} pts`} data-testid={`garden-stage-${i}`} className={`rounded-2xl p-1.5 text-center border ${current ? "border-amber-500 bg-amber-50" : reached ? "border-amber-200 bg-white" : "border-stone-100 bg-stone-50 opacity-50"}`}>
                  <GardenPlant stage={i} progress={reached ? 1 : 0} className="w-full h-12" animate={false} />
                  <div className="text-[9px] font-bold text-stone-700 truncate mt-1">{l.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
